import React from "react";
import { useSearchParams } from "react-router-dom";
import StandaloneHeader from "./StandaloneHeader";
import AppContent from "../AppContent";
import { recordDuration } from "../../utils/perfMetrics";

/**
 * Marks that this page session's FIRST patient open has been classified.
 *
 * A performance mark, not a module variable, because its lifetime is exactly
 * the one needed: the performance buffer belongs to the page session and is
 * reset by a real navigation. Only the first patient open of a page session
 * can possibly be a cold deep-link load, so the mark is set on that first
 * classification whichever regime it lands in — otherwise returning to the
 * deep-linked patient later in the session would emit a second "cold" sample
 * carrying the whole session age.
 */
const FIRST_PATIENT_OPEN_MARK = "pcp.first_patient_open";

/**
 * Regime A: the page was loaded with `?patient_id=` already in the URL, so
 * the elapsed time since page load IS the load-to-interactive figure.
 */
const COLD_DEEP_LINK_OP = "patient.load_to_interactive_cold_deep_link";

/**
 * Regime B: the app was already running (Home page, or another patient) and
 * the user opened this patient, so the clock starts at the patient switch —
 * time since page load would be human dwell time, not latency.
 */
const IN_SESSION_OP = "patient.load_to_interactive_in_session";

/**
 * Read the `patient_id` the document was originally loaded with.
 *
 * The navigation entry's `name` is the document URL — unlike a react-router
 * `setSearchParams`, which rewrites `window.location` without touching it. So
 * it answers "was this patient deep-linked at page load?" even after the SPA
 * has navigated. A runtime without Navigation Timing yields `null`, which
 * classifies conservatively as in-session (no sample beats a wrong one).
 *
 * @returns The deep-linked patient id, or null when there was none.
 */
function patientIdAtPageLoad(): string | null {
  const nav = performance.getEntriesByType("navigation")[0] as
    | PerformanceNavigationTiming
    | undefined;
  if (nav === undefined) return null;
  return new URL(nav.name, window.location.href).searchParams.get("patient_id");
}

/**
 * Classify one patient open, consuming the page session's one cold slot.
 *
 * @param patientId - The patient being opened.
 * @returns True when this is the page session's cold deep-link open.
 */
function isColdDeepLinkOpen(patientId: string): boolean {
  if (performance.getEntriesByName(FIRST_PATIENT_OPEN_MARK).length > 0) {
    return false;
  }
  performance.mark(FIRST_PATIENT_OPEN_MARK);
  return patientIdAtPageLoad() === patientId;
}

/**
 * Time one patient open through to the next painted frame (BLA-348).
 *
 * Neither regime is a true Time-to-Interactive — the tab's init fetches are
 * still in flight when the frame lands — but each is an honest proxy for a
 * distinct question, which is why they ship as two op names that must never
 * be averaged together: {@link COLD_DEEP_LINK_OP} measures time since page
 * load, {@link IN_SESSION_OP} measures time since the patient switch was
 * detected.
 *
 * A tab backgrounded mid-open must produce NO sample: the browser suspends
 * `requestAnimationFrame` while hidden and resumes it on refocus, so the frame
 * would otherwise land carrying the entire background dwell. Checking
 * `visibilityState` inside the callback cannot catch that — by the time it
 * runs the page is visible again — so the pending frame is instead cancelled
 * the moment the page goes hidden, which is the only point where that
 * transition is observable.
 *
 * @param patientId - The patient being opened.
 * @returns None.
 */
function scheduleOpenMeasurement(patientId: string): void {
  const coldDeepLink = isColdDeepLinkOpen(patientId);
  if (document.visibilityState === "hidden") return;
  const start = performance.now();
  const cancelIfHidden = (): void => {
    if (document.visibilityState !== "hidden") return;
    cancelAnimationFrame(frame);
    document.removeEventListener("visibilitychange", cancelIfHidden);
  };
  const frame = requestAnimationFrame(() => {
    document.removeEventListener("visibilitychange", cancelIfHidden);
    const now = performance.now();
    recordDuration(
      coldDeepLink ? COLD_DEEP_LINK_OP : IN_SESSION_OP,
      coldDeepLink ? now : now - start,
    );
  });
  document.addEventListener("visibilitychange", cancelIfHidden);
}

/**
 * Record a patient-open-to-interactive sample per opened patient.
 *
 * Fires during render (not from an effect) so the clock starts before this
 * shell's subtree renders — the render is the cost being measured. Keyed on
 * the patient id rather than on mount, because a direct patient A -> patient B
 * switch keeps this component mounted and only changes the prop.
 *
 * @param patientId - The active patient id.
 * @returns None.
 */
function useRecordPatientOpenToInteractive(patientId: string): void {
  const [timedPatientId, setTimedPatientId] = React.useState<string | null>(
    null,
  );
  if (timedPatientId !== patientId) {
    setTimedPatientId(patientId);
    scheduleOpenMeasurement(patientId);
  }
}

interface AppShellProps {
  patientId: string;
  encounterId: string | null;
  email: string;
  sessionId: string;
  onLogout: () => void;
}

const AppShell: React.FC<AppShellProps> = ({
  patientId,
  encounterId,
  email,
  sessionId,
  onLogout,
}) => {
  const [, setSearchParams] = useSearchParams();
  useRecordPatientOpenToInteractive(patientId);

  const handleBack = () => {
    setSearchParams({});
  };

  return (
    // `data-testid="patient-workspace"` is a TAB-INDEPENDENT readiness signal
    // for Tier-B e2e specs (BLA-335): this root mounts once per patient and
    // never depends on which tab is default/visible/hidden, unlike a
    // tab-button wait (BLA-322/328/332 all broke that shape when the default
    // or hidden tab set changed). Test-support attribute only, no behavior
    // change — see `frontend/e2e/helpers.ts`'s `openPatient()`.
    <div
      data-testid="patient-workspace"
      className="flex flex-col h-screen bg-gray-50"
    >
      <StandaloneHeader
        email={email}
        patientId={patientId}
        onBack={handleBack}
        onLogout={onLogout}
      />
      <main className="flex-1 overflow-auto">
        <AppContent
          patientId={patientId}
          encounterId={encounterId}
          userInfo={email}
          onLogout={onLogout}
          sessionId={sessionId}
          _accessToken={null}
        />
      </main>
    </div>
  );
};

export default AppShell;
